import type { ColumnDefinition } from '../components/ui/ColumnSelectorModal';

/** Columns hidden for manual welding views (pass list, segment view, segment detail). */
export const MANUAL_WELDING_HIDDEN_PARAMETER_KEYS = [
  'tilt',
  'tilt-range',
  'distance',
  'zone',
  'oscillation-width',
  'target',
  'horizontal-bias',
  'frequency',
  'total-wire-consumed',
  'true-energy',
  'arc-impedance',
] as const;

/** Basic-info columns shown only for manual welding pass views. */
export const MANUAL_WELDING_ONLY_COLUMN_KEYS = [
  'bug-number',
  'pipe-diameter',
  'band-diameter',
  'weld-length',
] as const;

export const MANUAL_WELDING_ONLY_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: 'bug-number', label: 'Bug Number', category: 'Basic Info' },
  { key: 'pipe-diameter', label: 'Pipe Diameter', category: 'Basic Info' },
  { key: 'band-diameter', label: 'Band Diameter', category: 'Basic Info' },
  { key: 'weld-length', label: 'Weld Length', category: 'Basic Info' },
];

const MANUAL_WELDING_COLUMNS_AFTER_BUG_TYPE: ColumnDefinition[] =
  MANUAL_WELDING_ONLY_COLUMN_DEFINITIONS.filter((col) => col.key !== 'weld-length');

const MANUAL_WELDING_WELD_LENGTH_COLUMN: ColumnDefinition =
  MANUAL_WELDING_ONLY_COLUMN_DEFINITIONS.find((col) => col.key === 'weld-length')!;

export const isManualWeldingHiddenColumn = (columnKey: string): boolean =>
  MANUAL_WELDING_HIDDEN_PARAMETER_KEYS.some(
    (key) => columnKey === key || columnKey.startsWith(`${key}-`)
  );

export const isManualWeldingOnlyColumn = (columnKey: string): boolean =>
  MANUAL_WELDING_ONLY_COLUMN_KEYS.some((key) => columnKey === key);

export const filterColumnsForManualWelding = (columns: string[]): string[] =>
  columns.filter((key) => !isManualWeldingHiddenColumn(key));

export const filterColumnsForNonManualWelding = (columns: string[]): string[] =>
  columns.filter((key) => !isManualWeldingOnlyColumn(key));

export const ensureManualWeldingOnlyColumns = (columns: string[]): string[] => {
  const next = filterColumnsForManualWelding(columns);
  MANUAL_WELDING_ONLY_COLUMN_KEYS.forEach((key) => {
    if (!next.includes(key)) {
      if (key === 'weld-length') {
        const weldTimeIndex = next.indexOf('weld-time');
        if (weldTimeIndex >= 0) {
          next.splice(weldTimeIndex + 1, 0, key);
          return;
        }
      }
      next.push(key);
    }
  });
  return next;
};

export const filterColumnDefinitionsForManualWelding = (
  columns: ColumnDefinition[]
): ColumnDefinition[] =>
  columns.filter((col) => !isManualWeldingHiddenColumn(col.key));

export const buildPassViewColumnDefinitionsForManualWelding = (
  columns: ColumnDefinition[]
): ColumnDefinition[] => {
  const filtered = filterColumnDefinitionsForManualWelding(columns).filter(
    (col) => !isManualWeldingOnlyColumn(col.key)
  );
  const bugTypeIndex = filtered.findIndex((col) => col.key === 'bug-type');
  const insertAfterBugType = bugTypeIndex >= 0 ? bugTypeIndex + 1 : filtered.length;

  const withBugTypeColumns = [
    ...filtered.slice(0, insertAfterBugType),
    ...MANUAL_WELDING_COLUMNS_AFTER_BUG_TYPE,
    ...filtered.slice(insertAfterBugType),
  ];

  const weldTimeIndex = withBugTypeColumns.findIndex((col) => col.key === 'weld-time');
  const insertAfterWeldTime =
    weldTimeIndex >= 0 ? weldTimeIndex + 1 : withBugTypeColumns.length;

  return [
    ...withBugTypeColumns.slice(0, insertAfterWeldTime),
    MANUAL_WELDING_WELD_LENGTH_COLUMN,
    ...withBugTypeColumns.slice(insertAfterWeldTime),
  ];
};

export const buildPassViewColumnDefinitionsForNonManualWelding = (
  columns: ColumnDefinition[]
): ColumnDefinition[] => columns.filter((col) => !isManualWeldingOnlyColumn(col.key));
/** Display-only bug type labels for manual welding views. Filter/data values stay unchanged. */
export const formatManualWeldingBugTypeLabel = (value?: string | null): string => {
  if (value == null || value === '' || value === '-') return '-';

  const normalized = value.trim().toLowerCase();
  if (normalized === 'counterclockwise') return 'CCW';
  if (normalized === 'clockwise') return 'CW';

  return value;
};
